import { useCallback, useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useUser } from '../hooks/useUser'
import { useQuery } from '@tanstack/react-query'
import {
    getProductsPricesAndAvailability,
    getUserProductsPricesAndAvailability,
} from '../queries'
import { useK2Setup } from '../hooks/useK2Setup'
import { Maybe, PricesAndAvailabilityProduct } from '../gql/graphql'
import { Language } from '@/utils/language'
import { EshopContext } from '../components/EshopProvider'
import { DEFAULT_LANGUAGE } from '@/constants'
import { Currency } from '@/utils/currency'

export const langToLocale: Record<Language, string> = {
    cs: 'cs-CZ',
    en: 'en-US',
    hu: 'hu-HU',
}

export function parsePrice(price: string | number) {
    return parseFloat(price?.toString())
}

export function formatPrice(
    price: string | number,
    currency: string,
    lang: string = 'cs_CZ',
    suffix?: string
) {
    const number = parsePrice(price)

    if (Number.isNaN(number)) {
        return '---'
    }

    return [
        new Intl.NumberFormat(lang, {
            style: 'currency',
            currency,
        }).format(number),
        suffix,
    ]
        .filter((x) => !!x)
        .join(' ')
}

export function useFormatPrice(
    currencyCode: Currency,
    onZero?: string,
    isZeroValid?: boolean
) {
    const { t } = useTranslation()
    const { lang } = useContext(EshopContext)

    return useCallback(
        (price: string | number, vat?: boolean, cc?: Currency) => {
            if (!isZeroValid && price === '0.00') {
                return onZero || t('eshop.price.uponInquiry')
            }
            let suffix = undefined
            if (typeof vat === 'boolean') {
                suffix = vat
                    ? t('eshop.price.vatSuffix')
                    : t('eshop.price.baseSuffix')
            }
            return formatPrice(
                price,
                cc || currencyCode,
                langToLocale[lang || DEFAULT_LANGUAGE],
                suffix
            )
        },
        [currencyCode, isZeroValid, lang, onZero, t]
    )
}

export function standardProductPriceVariant(
    p: Maybe<PricesAndAvailabilityProduct>
): Maybe<PricesAndAvailabilityProduct> {
    if (!p) {
        return p
    }
    return {
        ...p,
        abbr: p?.abbr?.replace(/\\/gi, '/'),
    }
}

export function useProductVariants(
    productGroupName: string,
    quality: string | null,
    brand?: string | null
) {
    const user = useUser()
    const k2 = useK2Setup()

    const anonymousPricesQueryKey = useMemo(
        () => [
            'product',
            'availability',
            'anonymous',
            productGroupName,
            quality,
            brand,
            k2.currency,
            k2.languageId,
        ],
        [productGroupName, quality, brand, k2.currency, k2.languageId]
    )

    const userPricesQueryKey = useMemo(
        () => [
            'product',
            'availability',
            productGroupName,
            quality,
            brand,
            k2.currency,
            k2.languageId,
        ],
        [productGroupName, quality, brand, k2.currency, k2.languageId]
    )

    const { data: anonymousData, isFetched: isAnonymousDataFetched } = useQuery(
        {
            queryKey: anonymousPricesQueryKey,
            queryFn: () =>
                getProductsPricesAndAvailability()({
                    brand,
                    quality,
                    sortingName: productGroupName,
                    languageId: k2.languageId,
                    currency: k2.currency,
                }).toPromise(),
            enabled: user?.isAnonymous,
        }
    )

    const { data: userData, isFetched: isUserDataFetched } = useQuery({
        queryKey: userPricesQueryKey,
        queryFn: () =>
            getUserProductsPricesAndAvailability()({
                brand,
                quality,
                sortingName: productGroupName,
                languageId: k2.languageId,
                currency: k2.currency,
            }).toPromise(),
        enabled: !user?.isAnonymous,
    })

    return useMemo(() => {
        if (user && !user.isAnonymous) {
            return {
                isFetched: isUserDataFetched,
                data: userData
                    ?.map?.((d) => d.pricesAndAvailabilityUser)
                    ?.map?.((v) => ({
                        ...v,
                        products: v?.products?.map(standardProductPriceVariant),
                    })),
            }
        }
        return {
            isFetched: isAnonymousDataFetched,
            data: anonymousData?.map?.((d) => d.pricesAndAvailability),
        }
    }, [
        user,
        anonymousData,
        isAnonymousDataFetched,
        userData,
        isUserDataFetched,
    ])
}
